import React, { useState, useEffect, useRef } from 'react';
import { Plus, GripVertical, Trash2, Play, Timer, Dumbbell, Pause, Hash, Milestone, Heart, Layers, Edit2, Check, ClipboardList, ChevronLeft, ChevronRight, X, Square, CheckCircle2 } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Reorder, useDragControls, AnimatePresence, motion } from 'framer-motion';
import Layout from '../components/Layout';
import ConfirmationModal from '../components/ConfirmationModal';
import { getExercises, getRoutines, getRoutine, updateRoutine, deleteRoutine, deleteWorkoutSession, updateWorkoutSession } from '../utils/api';
import { formatTime } from '../utils/time';

// Helper for classes
function cn(...inputs) {
  return inputs.filter(Boolean).join(' ');
}

// COMPONENTE SEPARADO PARA LOS CONTROLES DE SESIÓN - FUERA de RoutineBuilder
const SessionControlsComponent = ({
                                    activeSession,
                                    startSession,
                                    togglePauseSession,
                                    setShowTerminateModal,
                                    onNavigate,
                                    setCurrentRoutineCallback,
                                    routines,
                                    isFloating = false,
                                    isListMode = false
                                  }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const expandedWidth = activeSession?.isPaused ? 200 : 230;

    if (isOpen) {
      container.style.width = `${expandedWidth}px`;
    } else {
      container.style.width = '48px';
    }
  }, [isOpen, activeSession]);

  const handleToggleOpen = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(true);
  };

  const handleToggleClose = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(false);
  };

  const handleButtonClick = (callback) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (callback) callback();
    setIsOpen(false);
  };

  const handleStopClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(false);

    setTimeout(() => {
      setShowTerminateModal(true);
    }, 50);
  };

  if (!activeSession) return null;

  const controls = (
      <div
          ref={containerRef}
          style={{
            backgroundColor: '#A3E635',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            height: '48px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            width: '48px',
            transition: 'width 350ms linear',
            cursor: 'pointer',
          }}
          onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', height: '100%', minWidth: 'max-content' }}>
          {!isOpen ? (
              <button
                  onClick={handleToggleOpen}
                  style={{
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'black',
                    flexShrink: 0,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  type="button"
              >
                <ChevronLeft size={24} />
              </button>
          ) : (
              <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', paddingRight: '8px', whiteSpace: 'nowrap', height: '100%', flexShrink: 0 }}>
                <button
                    onClick={handleToggleClose}
                    style={{
                      padding: '8px',
                      color: 'black',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    type="button"
                >
                  <ChevronRight size={20} />
                </button>

                <div style={{ width: '1px', height: '24px', backgroundColor: 'rgba(0,0,0,0.2)' }} />

                <button
                    onClick={handleButtonClick(() => {
                      onNavigate('/workout');
                    })}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      paddingLeft: '8px',
                      paddingRight: '8px',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      color: 'black',
                      fontWeight: '900',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    type="button"
                >
                  <Play size={18} fill="black" /> CONTINUE
                </button>

                {!activeSession.isPaused && (
                    <button
                        onClick={handleButtonClick(() => {
                          togglePauseSession();
                        })}
                        style={{
                          padding: '8px',
                          color: 'black',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        type="button"
                    >
                      <Pause size={18} fill="black" />
                    </button>
                )}

                <button
                    onClick={handleStopClick}
                    style={{
                      padding: '8px',
                      color: '#dc2626',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    type="button"
                >
                  <Square size={18} fill="currentColor" />
                </button>
              </div>
          )}
        </div>
      </div>
  );

  if (isFloating) {
    return (
        <div
            style={{ position: 'absolute', bottom: '16px', right: '16px', zIndex: 50, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}
            onClick={(e) => e.stopPropagation()}
        >
          {controls}
        </div>
    );
  }

  return controls;
};

const RoutineStep = ({ step, index, displayIndex, exercises, removeStep, getStepIcon, editStep, moveIntoBlock, exercisesInSequence, isInsideReorder, dragControls: externalDragControls }) => {
  const internalDragControls = useDragControls();
  const dragControls = externalDragControls || internalDragControls;

  const exercise = exercises.find(ex => ex.id === step.exerciseId);
  const subtitle = step.type === 'break' ? 'Timed Rest' : (exercise?.subtitle || 'Exercise Step');
  const hasBreak = (step.type === 'exercise' || step.type === 'block') && step.breakDuration !== undefined;

  const content = (
      <div className={cn(
          "bg-[#1C1C1E] border border-[#2C2C2E] rounded-[24px] p-2 flex items-center gap-3 group cursor-auto touch-pan-y transition-all w-full",
          step.type === 'block' && "border-[#A3E635]/30 bg-[#1C1C1E]/50"
      )}>
        <div
            onPointerDown={(e) => {
              e.preventDefault();
              dragControls.start(e);
            }}
            className="text-slate-600 p-2 cursor-grab active:cursor-grabbing select-none touch-none"
        >
          <GripVertical size={18} />
        </div>

        <div className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
            step.type === 'break' ? "bg-blue-900/30 text-blue-400" :
                step.type === 'block' ? "bg-[#A3E635]/20 text-[#A3E635]" : "bg-[#2C2C2E] text-slate-400"
        )}>
          {getStepIcon(step)}
        </div>

        <div
            className="flex-1 cursor-pointer"
            onClick={() => (step.type === 'break' || hasBreak) && editStep(index)}
        >
          <h4 className="font-bold text-base leading-tight flex items-center gap-2">
            {step.type === 'block' ? (
                <>
                  {step.title}
                  <span className="text-[10px] bg-[#A3E635] text-black px-1.5 py-0.5 rounded-md font-black uppercase">BLOCK</span>
                  {hasBreak && <span className="text-xs text-slate-500 font-normal"> // {step.breakDuration}s break</span>}
                </>
            ) : step.type === 'break'
                ? `Break (${step.duration}s)`
                : (
                    <>
                      {displayIndex}. {step.title}
                      {hasBreak ? ` // ${step.breakDuration}s break` : ''}
                    </>
                )}
          </h4>
          <div className="flex flex-col gap-1 mt-1">
            <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">
              {step.type === 'block' ? `${exercisesInSequence.filter(s => s.blockId === step.id).length} exercises` : subtitle}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <div className="flex flex-col items-center gap-1">
            {step.sets > 1 && (
                <span
                    onClick={() => editStep(index)}
                    className="text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded-md font-black uppercase whitespace-nowrap cursor-pointer hover:bg-blue-600 transition-colors"
                >
              {step.sets} sets
            </span>
            )}
            {step.type === 'exercise' && (
                <select
                    value={step.blockId || ''}
                    onChange={(e) => moveIntoBlock(index, e.target.value || null)}
                    className="bg-[#2C2C2E] text-[10px] font-bold text-slate-400 border-none rounded-lg px-2 py-1 outline-none appearance-none cursor-pointer hover:bg-[#3C3C3E] focus:ring-1 focus:ring-[#A3E635]/50 transition-all"
                    title="Assign to block"
                >
                  <option value="">No Block</option>
                  {exercisesInSequence.filter(s => s.type === 'block' && s.id !== step.id).map(b => (
                      <option key={b.id} value={b.id}>{b.title}</option>
                  ))}
                </select>
            )}
            {step.type === 'block' && (
                <button
                    onClick={() => editStep(index, true)}
                    className="p-1 text-slate-600 hover:text-[#A3E635] transition-colors"
                    title="Edit block name"
                >
                  <Edit2 size={16} />
                </button>
            )}
          </div>
          <button
              onClick={() => removeStep(index)}
              className="p-2 text-slate-600 hover:text-red-500 transition-colors"
          >
            <Trash2 size={20} />
          </button>
        </div>
      </div>
  );

  if (isInsideReorder) {
    return (
        <div className="relative group/step">
          {content}
        </div>
    );
  }

  return (
      <div className="relative group/step">
        <Reorder.Item
            value={step}
            dragListener={false}
            dragControls={dragControls}
            className="w-full"
        >
          {content}
        </Reorder.Item>
      </div>
  );
};

const BlockItem = ({ step, index, displayIndex, exercises, removeStep, getStepIcon, editStep, moveIntoBlock, sequence, exerciseCount, onReorderChildren }) => {
  const dragControls = useDragControls();
  const blockChildren = sequence.filter(s => s.blockId === step.id);
  let localExerciseCount = exerciseCount;

  return (
      <Reorder.Item
          value={step}
          dragListener={false}
          dragControls={dragControls}
          className="space-y-3"
      >
        <RoutineStep
            step={step}
            index={index}
            displayIndex={displayIndex}
            exercises={exercises}
            removeStep={removeStep}
            getStepIcon={getStepIcon}
            editStep={editStep}
            moveIntoBlock={moveIntoBlock}
            exercisesInSequence={sequence}
            isInsideReorder={true}
            dragControls={dragControls}
        />
        <Reorder.Group
            axis="y"
            values={blockChildren}
            onReorder={(newOrder) => onReorderChildren(step.id, newOrder)}
            className="ml-8 pl-6 border-l-2 border-[#A3E635]/20 py-2 space-y-3"
        >
          {blockChildren.map((childStep) => {
            const originalIndex = sequence.findIndex(s => s.id === childStep.id);
            localExerciseCount++;
            const childDisplayIndex = localExerciseCount;
            return (
                <RoutineStep
                    key={childStep.id}
                    step={childStep}
                    index={originalIndex}
                    displayIndex={childDisplayIndex}
                    exercises={exercises}
                    removeStep={removeStep}
                    getStepIcon={getStepIcon}
                    editStep={editStep}
                    moveIntoBlock={moveIntoBlock}
                    exercisesInSequence={sequence}
                />
            );
          })}
          {blockChildren.length === 0 && (
              <div className="py-4 text-center border border-dashed border-[#2C2C2E] rounded-[20px] text-[10px] text-slate-500 font-black uppercase tracking-widest">
                Empty Block
              </div>
          )}
        </Reorder.Group>
      </Reorder.Item>
  );
};

const RoutineBuilder = () => {
  const [exercises, setExercises] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [currentRoutine, setCurrentRoutine] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [sequence, setSequence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [globalElapsed, setGlobalElapsed] = useState(0);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [showRoutineNameModal, setShowRoutineNameModal] = useState(false);
  const [showConfirmDeleteRoutine, setShowConfirmDeleteRoutine] = useState(false);
  const [editingBreakIndex, setEditingBreakIndex] = useState(null);
  const [editingNameIndex, setEditingNameIndex] = useState(null);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [tempDuration, setTempDuration] = useState(60);
  const [tempSets, setTempSets] = useState(1);
  const [tempName, setTempName] = useState('');
  const [tempRoutineName, setTempRoutineName] = useState('');
  const [savedBlocks, setSavedBlocks] = useState([]);
  const navigate = useNavigate();

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const todayName = daysOfWeek[new Date().getDay()];

  useEffect(() => {
    fetchInitialData();
    const saved = localStorage.getItem('active_session');
    if (saved) {
      setActiveSession(JSON.parse(saved));
    }
    const savedBlocksData = localStorage.getItem('saved_blocks');
    if (savedBlocksData) {
      setSavedBlocks(JSON.parse(savedBlocksData));
    }
  }, []);

  useEffect(() => {
    let interval;
    if (activeSession && !activeSession.isPaused) {
      interval = setInterval(() => {
        const now = Date.now();
        const pausedDuration = activeSession.totalPausedTime || 0;
        const globalSecs = Math.floor((now - activeSession.globalStartTime - pausedDuration) / 1000);
        setGlobalElapsed(globalSecs > 0 ? globalSecs : 0);
      }, 1000);
    } else if (activeSession && activeSession.isPaused) {
      const now = activeSession.pausedAt || Date.now();
      const pausedDuration = activeSession.totalPausedTime || 0;
      const globalSecs = Math.floor((now - activeSession.globalStartTime - pausedDuration) / 1000);
      setGlobalElapsed(globalSecs > 0 ? globalSecs : 0);
    } else {
      setGlobalElapsed(0);
    }
    return () => clearInterval(interval);
  }, [activeSession]);

  const fetchInitialData = async () => {
    try {
      const [exRes, routsRes] = await Promise.all([getExercises(), getRoutines()]);
      setExercises(exRes.data);
      setRoutines(routsRes.data);

      if (routsRes.data.length > 0) {
        setRoutines(routsRes.data);
      } else {
        await handleCreateRoutine(todayName, false);
      }
    } catch (error) {
      console.error('Failed to fetch initial data', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoutine = async (name, shouldEdit = true) => {
    try {
      setLoading(true);
      const res = await updateRoutine({ name, sequence: [] });
      const newRoutineId = res.data.id;
      const [exRes, routsRes] = await Promise.all([getExercises(), getRoutines()]);
      setExercises(exRes.data);
      setRoutines(routsRes.data);
      if (shouldEdit) {
        const newRoutine = routsRes.data.find(r => r.id == newRoutineId);
        setCurrentRoutine(newRoutine);
        setSequence(newRoutine.sequence || []);
        setViewMode('editor');
      }
    } catch (error) {
      console.error('Failed to create routine', error);
    } finally {
      setLoading(false);
    }
  };

  const switchRoutine = async (routineId) => {
    const routine = routines.find(r => r.id == routineId);
    if (routine) {
      setCurrentRoutine(routine);
      setSequence(routine.sequence || []);
      setViewMode('editor');
    }
  };

  const handleRenameRoutine = async () => {
    if (!currentRoutine || !tempRoutineName) return;
    try {
      await updateRoutine({
        id: currentRoutine.id,
        name: tempRoutineName,
        sequence: currentRoutine.sequence
      });
      const routsRes = await getRoutines();
      setRoutines(routsRes.data);
      setCurrentRoutine({ ...currentRoutine, name: tempRoutineName });
      setShowRoutineNameModal(false);
    } catch (error) {
      console.error('Failed to rename routine', error);
    }
  };

  const handleDeleteRoutine = async () => {
    if (!currentRoutine) return;
    try {
      await deleteRoutine(currentRoutine.id);
      const routsRes = await getRoutines();
      setRoutines(routsRes.data);
      if (routsRes.data.length > 0) {
        setCurrentRoutine(routsRes.data[0]);
        setSequence(routsRes.data[0].sequence || []);
        setViewMode('list');
      } else {
        await handleCreateRoutine(todayName, false);
        setViewMode('list');
      }
      setShowConfirmDeleteRoutine(false);
    } catch (error) {
      console.error('Failed to delete routine', error);
    }
  };

  const handleReorderChildren = async (blockId, newChildren) => {
    const newSequence = [...sequence];

    const childIndices = [];
    newSequence.forEach((item, idx) => {
      if (item.blockId === blockId) {
        childIndices.push(idx);
      }
    });

    if (childIndices.length > 0) {
      newChildren.forEach((child, i) => {
        newSequence[childIndices[i]] = child;
      });
    }

    setSequence(newSequence);
    if (currentRoutine) {
      try {
        await updateRoutine({
          id: currentRoutine.id,
          name: currentRoutine.name,
          sequence: newSequence
        });
        setRoutines(prev => prev.map(r => r.id === currentRoutine.id ? { ...r, sequence: newSequence } : r));
        setCurrentRoutine(prev => ({ ...prev, sequence: newSequence }));
      } catch (error) {
        console.error('Failed to update routine', error);
      }
    }
  };

  const handleSave = async (newTopLevelSequence, fullSequenceWithChildren = null) => {
    let fullSequence = [];
    if (fullSequenceWithChildren) {
      fullSequence = fullSequenceWithChildren;
    } else {
      newTopLevelSequence.forEach(item => {
        fullSequence.push(item);
        if (item.type === 'block') {
          const children = sequence.filter(s => s.blockId === item.id);
          fullSequence.push(...children);
        }
      });
    }

    setSequence(fullSequence);
    if (currentRoutine) {
      try {
        await updateRoutine({
          id: currentRoutine.id,
          name: currentRoutine.name,
          sequence: fullSequence
        });
        setRoutines(prev => prev.map(r => r.id === currentRoutine.id ? { ...r, sequence: fullSequence } : r));
        setCurrentRoutine(prev => ({ ...prev, sequence: fullSequence }));
      } catch (error) {
        console.error('Failed to update routine', error);
      }
    }
  };

  const addExercise = (exercise) => {
    const newStep = {
      type: 'exercise',
      exerciseId: exercise.id,
      title: exercise.title,
      breakDuration: 60,
      sets: 1,
      id: `step-${Date.now()}-${Math.random()}`,
      blockId: null
    };

    const newSequence = [...sequence, newStep];
    handleSave(null, newSequence);
  };

  const addBlock = () => {
    const blockLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const existingBlocksCount = sequence.filter(s => s.type === 'block').length;
    const blockLetter = blockLetters[existingBlocksCount % blockLetters.length];

    const newBlock = {
      type: 'block',
      id: `block-${Date.now()}-${Math.random()}`,
      title: `Block ${blockLetter}`,
      breakDuration: 60
    };

    const newSequence = [...sequence, newBlock];
    handleSave(null, newSequence);
  };

  const addSavedBlock = (savedBlock) => {
    const newBlockId = `block-${Date.now()}-${Math.random()}`;

    const blockToInsert = {
      ...savedBlock,
      id: newBlockId,
      blockId: null
    };
    delete blockToInsert.children;

    const childrenToInsert = (savedBlock.children || []).map(child => ({
      ...child,
      id: `${child.type}-${Date.now()}-${Math.random()}`,
      blockId: newBlockId
    }));

    const newSequence = [...sequence, blockToInsert, ...childrenToInsert];
    const topLevelOnly = newSequence.filter(s => !s.blockId);
    handleSave(topLevelOnly, newSequence);
  };

  const addBreak = () => {
    const newStep = {
      type: 'break',
      duration: 60,
      id: `break-${Date.now()}-${Math.random()}`
    };
    const newSequence = [...sequence, newStep];
    setSequence(newSequence);
    setEditingBreakIndex(newSequence.length - 1);
    setTempDuration(60);
    setShowDurationModal(true);
  };

  const editStep = (index, editName = false) => {
    const step = sequence[index];
    if (editName && step.type === 'block') {
      setEditingNameIndex(index);
      setTempName(step.title);
      setShowNameModal(true);
      return;
    }

    if (step.type === 'break') {
      setEditingBreakIndex(index);
      setTempDuration(step.duration);
      setTempSets(1);
      setShowDurationModal(true);
    } else if ((step.type === 'exercise' || step.type === 'block') && step.breakDuration !== undefined) {
      setEditingBreakIndex(index);
      setTempDuration(step.breakDuration);
      setTempSets(step.sets || 1);
      setShowDurationModal(true);
    }
  };

  const handleNameConfirm = async () => {
    const newSequence = [...sequence];
    const step = newSequence[editingNameIndex];
    if (step.type === 'block') {
      step.title = tempName.trim() || step.title;
    }
    await handleSave(null, newSequence);
    setShowNameModal(false);
    setEditingNameIndex(null);
  };

  const handleSaveBlockAsElement = () => {
    const blockStep = sequence[editingNameIndex];
    if (!blockStep || blockStep.type !== 'block') return;

    const children = sequence.filter(s => s.blockId === blockStep.id).map(c => {
      const cleanChild = { ...c };
      delete cleanChild.id;
      return cleanChild;
    });

    const blockToSave = {
      ...blockStep,
      title: tempName.trim() || blockStep.title,
      id: undefined,
      children
    };

    const newSavedBlocks = [...savedBlocks, blockToSave];
    setSavedBlocks(newSavedBlocks);
    localStorage.setItem('saved_blocks', JSON.stringify(newSavedBlocks));
    setShowNameModal(false);
    setEditingNameIndex(null);
  };

  const deleteSavedBlock = (index) => {
    const newSavedBlocks = [...savedBlocks];
    newSavedBlocks.splice(index, 1);
    setSavedBlocks(newSavedBlocks);
    localStorage.setItem('saved_blocks', JSON.stringify(newSavedBlocks));
  };

  const handleDurationConfirm = async () => {
    const newSequence = [...sequence];
    const step = newSequence[editingBreakIndex];
    if (step.type === 'break') {
      step.duration = parseInt(tempDuration) || 60;
    } else {
      step.breakDuration = parseInt(tempDuration) || 0;
      step.sets = parseInt(tempSets) || 1;
    }
    await handleSave(null, newSequence);
    setShowDurationModal(false);
    setEditingBreakIndex(null);
  };

  const removeStep = (index) => {
    setDeleteAction({
      type: 'step',
      index,
      title: 'Remove Step?',
      message: 'Are you sure you want to remove this step from your routine?'
    });
    setShowConfirmDelete(true);
  };

  const confirmRemoveStep = (index) => {
    const newSequence = [...sequence];
    const removedStep = newSequence[index];

    if (removedStep.type === 'block') {
      const filteredSequence = newSequence.filter(s => s.id !== removedStep.id && s.blockId !== removedStep.id);
      handleSave(null, filteredSequence);
    } else {
      newSequence.splice(index, 1);
      handleSave(null, newSequence);
    }
  };

  const confirmDeleteSession = () => {
    localStorage.removeItem('active_session');
    window.dispatchEvent(new Event('storage'));
    setActiveSession(null);
  };

  const startSession = () => {
    if (activeSession) {
      if (activeSession.routineId == currentRoutine?.id) {
        if (activeSession.isPaused) {
          const now = Date.now();
          const updatedSession = { ...activeSession };
          const pausedDuration = now - activeSession.pausedAt;
          updatedSession.totalPausedTime = (activeSession.totalPausedTime || 0) + pausedDuration;
          updatedSession.isPaused = false;
          updatedSession.pausedAt = null;
          localStorage.setItem('active_session', JSON.stringify(updatedSession));
          window.dispatchEvent(new Event('storage'));
        }
        navigate('/workout');
      } else {
        setDeleteAction({
          type: 'new_session',
          title: 'Start New Routine?',
          message: 'Another routine is currently active. Starting this one will delete your current progress. Are you sure?'
        });
        setShowConfirmDelete(true);
      }
    } else {
      if (currentRoutine) {
        localStorage.setItem('active_session', JSON.stringify({
          routineId: currentRoutine.id,
          globalStartTime: Date.now(),
          currentIndex: 0
        }));
        window.dispatchEvent(new Event('storage'));
        navigate('/workout');
      }
    }
  };

  const togglePauseSession = () => {
    if (!activeSession) return;

    const now = Date.now();
    const updatedSession = { ...activeSession };

    if (activeSession.isPaused) {
      const pausedDuration = now - activeSession.pausedAt;
      updatedSession.totalPausedTime = (activeSession.totalPausedTime || 0) + pausedDuration;
      updatedSession.isPaused = false;
      updatedSession.pausedAt = null;
    } else {
      updatedSession.isPaused = true;
      updatedSession.pausedAt = now;
    }

    localStorage.setItem('active_session', JSON.stringify(updatedSession));
    window.dispatchEvent(new Event('storage'));
    setActiveSession(updatedSession);
  };

  const handleFinishConfirm = async (save) => {
    const isExpired = globalElapsed > 28800;

    if (!save || isExpired) {
      if (activeSession?.globalStartTime) {
        await deleteWorkoutSession(activeSession.globalStartTime.toString());
      }
      if (isExpired) {
        alert("Session expired (longer than 8 hours) and was not saved.");
      }
      confirmDeleteSession();
    } else {
      if (activeSession?.globalStartTime) {
        await updateWorkoutSession(activeSession.globalStartTime.toString(), {
          total_duration: globalElapsed,
          routine_name: currentRoutine?.name || 'Routine',
          routine_sequence: JSON.stringify(sequence)
        });
      }
      confirmDeleteSession();
    }
    setShowFinishModal(false);
  };

  const moveIntoBlock = (index, blockId) => {
    const newSequence = [...sequence];
    newSequence[index].blockId = blockId;
    handleSave(null, newSequence);
  };

  useEffect(() => {
    if (activeSession) {
      const updated = { ...activeSession, globalElapsed: globalElapsed };
      localStorage.setItem('active_session', JSON.stringify(updated));
    }
  }, [globalElapsed]);

  const handleConfirmDelete = () => {
    if (deleteAction.type === 'step') {
      confirmRemoveStep(deleteAction.index);
    } else if (deleteAction.type === 'session') {
      confirmDeleteSession();
    } else if (deleteAction.type === 'new_session') {
      localStorage.removeItem('active_session');
      window.dispatchEvent(new Event('storage'));
      localStorage.setItem('active_session', JSON.stringify({
        routineId: currentRoutine.id,
        globalStartTime: Date.now(),
        currentIndex: 0
      }));
      window.dispatchEvent(new Event('storage'));
      navigate('/workout');
    }
    setShowConfirmDelete(false);
    setDeleteAction(null);
  };

  const getStepIcon = (step) => {
    if (step.type === 'break') return <Timer size={20} />;
    if (step.type === 'block') return <Layers size={20} />;

    const exercise = exercises.find(ex => ex.id === step.exerciseId);
    if (!exercise) return <Dumbbell size={20} />;

    try {
      const config = JSON.parse(exercise.activity_log_config || '{}');
      if (config.weight) return <Dumbbell size={20} />;
      if (config.reps) return <Hash size={20} />;
      if (config.time) return <Timer size={20} />;
      if (config.distance) return <Milestone size={20} />;
      if (config.bpm) return <Heart size={20} />;
    } catch (e) {
      console.error('Failed to parse activity log config', e);
    }
    return <Dumbbell size={20} />;
  };

  if (loading) return <Layout>Loading...</Layout>;

  // Contenido de la vista de lista
  const listContent = (
      <Layout title="ROUTINES" subtitle="Manage your workout collections">
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-black italic tracking-tighter uppercase">Available Routines</h2>
            <button
                onClick={() => handleCreateRoutine(`Routine ${routines.length + 1}`)}
                className="bg-[#A3E635] text-black px-6 py-3 rounded-full font-black text-xs flex items-center gap-2 hover:scale-105 transition-all"
            >
              <Plus size={16} /> NEW ROUTINE
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {routines.map(r => (
                <div
                    key={r.id}
                    className="bg-[#1C1C1E] border border-[#2C2C2E] rounded-[32px] p-6 hover:border-[#A3E635] transition-all group relative cursor-pointer shadow-lg"
                    onClick={() => switchRoutine(r.id)}
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex justify-between items-start">
                      <div className="w-12 h-12 rounded-full bg-[#2C2C2E] flex items-center justify-center text-[#A3E635]">
                        {activeSession && activeSession.routineId == r.id ? (
                            <Timer size={24} />
                        ) : (
                            <ClipboardList size={24} />
                        )}
                      </div>
                      {activeSession && activeSession.routineId == r.id && (
                          <div className="flex items-center ml-2 h-12">
                            <span className="text-white font-black italic text-[16px] leading-none">{formatTime(globalElapsed)}</span>
                          </div>
                      )}
                      <div className="flex-1" />
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentRoutine(r);
                              setTempRoutineName(r.name);
                              setShowRoutineNameModal(true);
                            }}
                            className="p-2 text-slate-500 hover:text-[#A3E635] transition-colors"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentRoutine(r);
                              setShowConfirmDeleteRoutine(true);
                            }}
                            className="p-2 text-slate-500 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-black italic tracking-tighter uppercase truncate">{r.name}</h3>
                      <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">
                        {r.sequence?.filter(s => s.type !== 'break').length || 0} Exercises
                      </p>
                    </div>
                  </div>

                  {activeSession && activeSession.routineId == r.id && (
                      <div className="absolute bottom-4 right-4 z-[50]">
                        <SessionControlsComponent
                            activeSession={activeSession}
                            startSession={startSession}
                            togglePauseSession={togglePauseSession}
                            setShowTerminateModal={setShowTerminateModal}
                            onNavigate={navigate}
                            setCurrentRoutineCallback={setCurrentRoutine}
                            routines={routines}
                            isFloating={true}
                            isListMode={true}
                        />
                      </div>
                  )}
                </div>
            ))}
          </div>
        </div>
      </Layout>
  );

  // Contenido de la vista de editor
  const editorContent = (
      <Layout title="ROUTINE BUILDER" subtitle={`Editing: ${currentRoutine?.name}`}>
        <div className="mb-6">
          <button
              onClick={() => setViewMode('list')}
              className="text-slate-500 hover:text-[#A3E635] font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-colors"
          >
            ← Back to Routines
          </button>
        </div>
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1 space-y-4">
            <div className="flex justify-between items-center mb-6 relative">
              <h2 className="text-2xl font-black italic tracking-tighter uppercase shrink-0">
                {activeSession && activeSession.routineId == currentRoutine?.id ? (
                    <div className="flex items-center gap-3">
                      <Timer size={24} className="text-[#A3E635]" />
                      <span>{formatTime(globalElapsed)}</span>
                    </div>
                ) : (
                    currentRoutine?.name || 'Sequence'
                )}
              </h2>
              <div className="flex gap-2 items-center">
                <SessionControlsComponent
                    activeSession={activeSession && activeSession.routineId == currentRoutine?.id ? activeSession : null}
                    startSession={startSession}
                    togglePauseSession={togglePauseSession}
                    setShowTerminateModal={setShowTerminateModal}
                    onNavigate={navigate}
                    setCurrentRoutineCallback={setCurrentRoutine}
                    routines={routines}
                    isFloating={false}
                    isListMode={false}
                />
                {(!activeSession || activeSession.routineId != currentRoutine?.id) && (
                    <button
                        onClick={startSession}
                        disabled={sequence.length === 0}
                        className="bg-[#A3E635] text-black px-8 py-3 rounded-full font-black flex items-center gap-2 hover:scale-105 transition-transform disabled:opacity-50 disabled:scale-100"
                    >
                      <Play size={20} fill="black" /> START SESSION
                    </button>
                )}
              </div>
            </div>

            <Reorder.Group
                axis="y"
                values={sequence.filter(s => !s.blockId)}
                onReorder={handleSave}
                className="space-y-3"
            >
              {(() => {
                let exerciseCount = 0;
                const topLevelSteps = sequence.filter(s => !s.blockId);
                return topLevelSteps.map((step) => {
                  const index = sequence.findIndex(s => s.id === step.id);
                  let displayIndex = null;
                  if (step.type === 'exercise') {
                    exerciseCount++;
                    displayIndex = exerciseCount;
                  }
                  const stableKey = step.id || `step-${index}`;
                  if (step.type === 'block') {
                    const blockItem = (
                        <BlockItem
                            key={stableKey}
                            step={step}
                            index={index}
                            displayIndex={displayIndex}
                            exercises={exercises}
                            removeStep={removeStep}
                            getStepIcon={getStepIcon}
                            editStep={editStep}
                            moveIntoBlock={moveIntoBlock}
                            sequence={sequence}
                            exerciseCount={exerciseCount}
                            onReorderChildren={handleReorderChildren}
                        />
                    );
                    exerciseCount += sequence.filter(s => s.blockId === step.id).length;
                    return blockItem;
                  }
                  return (
                      <RoutineStep
                          key={stableKey}
                          step={step}
                          index={index}
                          displayIndex={displayIndex}
                          exercises={exercises}
                          removeStep={removeStep}
                          getStepIcon={getStepIcon}
                          editStep={editStep}
                          moveIntoBlock={moveIntoBlock}
                          exercisesInSequence={sequence}
                      />
                  );
                });
              })()}
            </Reorder.Group>

            {sequence.length === 0 && (
                <div className="py-20 text-center border-2 border-dashed border-[#2C2C2E] rounded-[32px]">
                  <p className="text-slate-500 font-bold uppercase text-sm">Your routine is empty</p>
                  {exercises.length === 0 ? (
                      <p className="text-slate-600 text-xs mt-1 px-4">
                        First, <Link to="/exercises" className="text-[#A3E635] hover:underline">create new exercises</Link> in the library.
                      </p>
                  ) : (
                      <p className="text-slate-600 text-xs mt-1">Add exercises from the library on the right</p>
                  )}
                </div>
            )}
          </div>

          <div className="w-full lg:w-80 space-y-6">
            <div className="bg-[#1C1C1E] border border-[#2C2C2E] rounded-[32px] p-6">
              <h3 className="text-lg font-black italic tracking-tighter uppercase mb-6">Add to Routine</h3>
              <button
                  onClick={addBlock}
                  className="w-full bg-[#A3E635] text-black p-3 rounded-2xl font-bold flex items-center gap-3 hover:scale-105 transition-all mb-4 text-sm"
              >
                <Layers size={18} /> + Add Block
              </button>

              <div className="border-t border-[#2C2C2E] pt-4">
                <p className="text-[10px] text-slate-500 font-black uppercase mb-4 tracking-widest">Exercise Library (tap to add)</p>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                  {exercises.map(ex => (
                      <button
                          key={ex.id}
                          onClick={() => addExercise(ex)}
                          className="w-full text-left bg-[#121214] border border-[#2C2C2E] p-3 rounded-xl hover:border-[#A3E635] transition-colors group"
                      >
                        <span className="font-bold text-sm block truncate">{ex.title}</span>
                      </button>
                  ))}
                </div>
              </div>

              {savedBlocks.length > 0 && (
                  <div className="border-t border-[#2C2C2E] mt-4 pt-4">
                    <p className="text-[10px] text-slate-500 font-black uppercase mb-4 tracking-widest">Saved Blocks</p>
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                      {savedBlocks.map((block, idx) => (
                          <div key={idx} className="flex gap-1 group">
                            <button
                                onClick={() => addSavedBlock(block)}
                                className="flex-1 text-left bg-[#1C1C1E] border border-[#A3E635]/30 p-3 rounded-xl hover:border-[#A3E635] transition-colors overflow-hidden"
                            >
                              <span className="font-bold text-[11px] block truncate text-[#A3E635] uppercase">{block.title}</span>
                              <span className="text-[9px] text-slate-500">{block.children?.length || 0} exercises</span>
                            </button>
                            <button
                                onClick={() => deleteSavedBlock(idx)}
                                className="p-2 text-slate-600 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                                title="Delete saved block"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                      ))}
                    </div>
                  </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
  );

  // Modales comunes para ambos modos
  const modals = (
      <>
        <ConfirmationModal
            isOpen={showConfirmDelete}
            onClose={() => setShowConfirmDelete(false)}
            onConfirm={handleConfirmDelete}
            title={deleteAction?.title}
            message={deleteAction?.message}
        />

        <ConfirmationModal
            isOpen={showConfirmDeleteRoutine}
            onClose={() => setShowConfirmDeleteRoutine(false)}
            onConfirm={handleDeleteRoutine}
            title="Delete Routine"
            message={`Are you sure you want to delete the routine "${currentRoutine?.name}"? This action cannot be undone.`}
        />

        {showRoutineNameModal && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
              <div className="bg-[#1C1C1E] border border-[#2C2C2E] rounded-[32px] p-8 w-full max-w-md shadow-2xl">
                <h3 className="text-xl font-black italic uppercase mb-6 tracking-tight text-white">Rename Routine</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] text-slate-500 font-black uppercase tracking-widest ml-1">Routine Name</label>
                    <input
                        autoFocus
                        type="text"
                        value={tempRoutineName}
                        onChange={(e) => setTempRoutineName(e.target.value)}
                        className="w-full bg-[#2C2C2E] border-none rounded-2xl px-5 py-4 text-white font-bold outline-none focus:ring-2 focus:ring-[#A3E635]/50 transition-all"
                        placeholder="Enter routine name..."
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                        onClick={() => setShowRoutineNameModal(false)}
                        className="flex-1 bg-[#2C2C2E] text-slate-400 py-4 rounded-full font-black text-xs uppercase hover:bg-[#3C3C3E] transition-all"
                    >
                      Cancel
                    </button>
                    <button
                        onClick={handleRenameRoutine}
                        className="flex-1 bg-[#A3E635] text-black py-4 rounded-full font-black text-xs uppercase hover:scale-105 transition-all shadow-lg shadow-[#A3E635]/20"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}

        <ConfirmationModal
            isOpen={showTerminateModal}
            onClose={() => setShowTerminateModal(false)}
            onConfirm={() => {
              setShowTerminateModal(false);
              setShowFinishModal(true);
            }}
            title="Workout Ended"
            message="Would you like to terminate the routine?"
            confirmText="Yes, end routine"
            cancelText="No, continue routine"
            variant="primary"
        />

        {showFinishModal && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center justify-center p-6">
              <div className="bg-[#1C1C1E] border border-[#2C2C2E] rounded-[40px] p-10 max-w-sm w-full text-center space-y-8 animate-in fade-in zoom-in duration-300">
                <div className="w-20 h-20 bg-[#A3E6351A] text-[#A3E635] rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 size={40} />
                </div>
                <div className="space-y-2">
                  <h3 className="text-3xl font-black italic tracking-tighter uppercase">Workout Ended</h3>
                  <p className="text-slate-500 font-bold uppercase tracking-widest text-sm">
                    Total Time: <span className="text-white">{formatTime(globalElapsed)}</span>
                  </p>
                </div>
                <p className="text-slate-400 font-medium">Would you like to add this session in the Session History?</p>
                <div className="grid gap-3">
                  <button
                      onClick={() => handleFinishConfirm(true)}
                      className="w-full bg-[#A3E635] text-black py-4 rounded-full font-black uppercase tracking-widest hover:scale-105 transition-transform"
                  >
                    Yes, Save Session
                  </button>
                  <button
                      onClick={() => handleFinishConfirm(false)}
                      className="w-full bg-[#2C2C2E] text-white py-4 rounded-full font-black uppercase tracking-widest hover:bg-[#3C3C3E] transition-colors"
                  >
                    No, Discard
                  </button>
                </div>
              </div>
            </div>
        )}

        {showDurationModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowDurationModal(false)} />
              <div className="relative bg-[#1C1C1E] border border-[#2C2C2E] rounded-[40px] w-full max-w-md p-8 animate-in zoom-in duration-200 text-center">
                {sequence[editingBreakIndex]?.type === 'exercise' && (
                    <>
                      <h3 className="text-2xl font-black italic uppercase tracking-tighter mb-2">Sets</h3>
                      <p className="text-slate-500 font-bold uppercase tracking-widest text-xs mb-8">How many times to perform this exercise</p>
                      <div className="flex justify-center items-center gap-4 mb-8">
                        <button onClick={() => setTempSets(Math.max(1, tempSets - 1))} className="w-12 h-12 rounded-full bg-[#2C2C2E] flex items-center justify-center text-white hover:bg-[#3C3C3E]">-1</button>
                        <div className="flex flex-col items-center">
                          <input type="number" value={tempSets} onChange={(e) => setTempSets(parseInt(e.target.value) || 1)} className="bg-transparent border-b-2 border-[#A3E635] text-4xl font-black text-center w-24 outline-none" />
                          <span className="text-[10px] text-slate-500 font-black uppercase mt-1">Sets</span>
                        </div>
                        <button onClick={() => setTempSets(tempSets + 1)} className="w-12 h-12 rounded-full bg-[#2C2C2E] flex items-center justify-center text-white hover:bg-[#3C3C3E]">+1</button>
                      </div>
                      <div className="h-px bg-[#2C2C2E] w-full mb-8" />
                    </>
                )}
                <h3 className="text-2xl font-black italic uppercase tracking-tighter mb-2">Rest Duration</h3>
                <p className="text-slate-500 font-bold uppercase tracking-widest text-xs mb-8">Set how many seconds for this break</p>
                <div className="flex justify-center items-center gap-4 mb-8">
                  <button onClick={() => setTempDuration(Math.max(0, tempDuration - 5))} className="w-12 h-12 rounded-full bg-[#2C2C2E] flex items-center justify-center text-white hover:bg-[#3C3C3E]">-5</button>
                  <div className="flex flex-col items-center">
                    <input type="number" value={tempDuration} onChange={(e) => setTempDuration(parseInt(e.target.value) || 0)} className="bg-transparent border-b-2 border-[#A3E635] text-4xl font-black text-center w-24 outline-none" autoFocus />
                    <span className="text-[10px] text-slate-500 font-black uppercase mt-1">Seconds</span>
                  </div>
                  <button onClick={() => setTempDuration(tempDuration + 5)} className="w-12 h-12 rounded-full bg-[#2C2C2E] flex items-center justify-center text-white hover:bg-[#3C3C3E]">+5</button>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full">
                  <button onClick={handleDurationConfirm} className="flex-1 py-4 rounded-full font-black uppercase tracking-widest text-sm bg-[#A3E635] text-black hover:scale-105 transition-all">Save</button>
                  <button onClick={() => setShowDurationModal(false)} className="flex-1 py-4 rounded-full font-black uppercase tracking-widest text-sm bg-[#2C2C2E] text-slate-300 hover:bg-[#3C3C3E] transition-all">Cancel</button>
                </div>
              </div>
            </div>
        )}

        {showNameModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowNameModal(false)} />
              <div className="relative bg-[#1C1C1E] border border-[#2C2C2E] rounded-[40px] w-full max-w-md p-8 animate-in zoom-in duration-200 text-center">
                <h3 className="text-2xl font-black italic uppercase tracking-tighter mb-2">Block Name</h3>
                <p className="text-slate-500 font-bold uppercase tracking-widest text-xs mb-8">Customize your block label</p>
                <input type="text" value={tempName} onChange={(e) => setTempName(e.target.value)} className="bg-[#2C2C2E] border-none rounded-2xl text-xl font-black text-center w-full p-4 outline-none mb-8 focus:ring-2 focus:ring-[#A3E635]" placeholder="Enter block name..." autoFocus onKeyDown={(e) => e.key === 'Enter' && handleNameConfirm()} />
                <div className="flex flex-col sm:flex-row gap-3 w-full">
                  <button onClick={handleNameConfirm} className="flex-1 py-4 rounded-full font-black uppercase tracking-widest text-sm bg-[#A3E635] text-black hover:scale-105 transition-all">Save</button>
                  <button onClick={handleSaveBlockAsElement} className="flex-1 py-4 rounded-full font-black uppercase tracking-widest text-[10px] bg-blue-600 text-white hover:scale-105 transition-all flex items-center justify-center gap-1" title="Save this block as a reusable element"><Layers size={14} /> Save as Template</button>
                  <button onClick={() => setShowNameModal(false)} className="flex-1 py-4 rounded-full font-black uppercase tracking-widest text-sm bg-[#2C2C2E] text-slate-300 hover:bg-[#3C3C3E] transition-all">Cancel</button>
                </div>
              </div>
            </div>
        )}
      </>
  );

  return (
      <>
        {viewMode === 'list' ? listContent : editorContent}
        {modals}
      </>
  );
};

export default RoutineBuilder;